import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Divider,
    Grid,
    Card,
    CardContent,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    CircularProgress,
    useTheme,
    Stack,
    InputAdornment,
    useMediaQuery,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Search as SearchIcon,
    ArrowUpward,
    ArrowDownward,
    UnfoldMore
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import api from '../../../services/api';
import Paginate from '../../../components/Paginate';

const itemsPerPage = 6;

const NeighborhoodPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [neighborhoods, setNeighborhoods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentNeighborhood, setCurrentNeighborhood] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        zone: ''
    });
    const [originalFormData, setOriginalFormData] = useState({
        name: '',
        zone: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [page, setPage] = useState(1);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [neighborhoodToDeleteId, setNeighborhoodToDeleteId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending',
    });

    useEffect(() => {
        fetchAllNeighborhoods();
    }, []);

    const fetchAllNeighborhoods = async () => {
        try {
            setLoading(true);
            const response = await api.get('/neighborhood');
            setNeighborhoods(response.data);
        } catch (error) {
            showSnackbar('Erro ao carregar bairros ou distritos', 'error');
            console.error('Error fetching neighborhoods:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const visibleNeighborhood = useMemo(() => {
        const normalizeString = (str) => {
            return str
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
                .replace(/\s+/g, '');
        };

        let filteredNeighborhood = neighborhoods.filter(neighborhood =>
            normalizeString(neighborhood.name).includes(normalizeString(searchTerm))
        );

        if (sortConfig.key) {
            filteredNeighborhood.sort((a, b) => {
                const aValue = normalizeString(a[sortConfig.key]);
                const bValue = normalizeString(b[sortConfig.key]);

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredNeighborhood.slice(startIndex, endIndex);
    }, [neighborhoods, page, searchTerm, sortConfig]);

    const handleOpenDialog = (neighborhood = null) => {
        setCurrentNeighborhood(neighborhood);
        const newFormData = {
            name: neighborhood ? neighborhood.name : '',
            zone: neighborhood ? neighborhood.zone : '',
        };
        setFormData(newFormData);
        setOriginalFormData(newFormData);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentNeighborhood(null);
        setFormData({ name: '', zone: '' });
        setOriginalFormData({ name: '', zone: '' });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            if (currentNeighborhood) {
                await api.put(`/neighborhood/${currentNeighborhood.id}`, formData);
                showSnackbar('Bairro ou distrito atualizado com sucesso.', 'success');
            } else {
                await api.post('/neighborhood', formData);
                showSnackbar('Bairro ou distrito cadastrado com sucesso.', 'success');
            }
            fetchAllNeighborhoods();
            handleCloseDialog();
        } catch (error) {
            const message = error.response?.data?.error || 'Erro ao processar o bairro ou distrito';
            showSnackbar(message, 'error');
            console.error('Error submitting neighborhood:', error);
        }
    };

    const handleDelete = (id) => {
        setNeighborhoodToDeleteId(id);
        setOpenConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await api.delete(`/neighborhood/${neighborhoodToDeleteId}`);
            showSnackbar('Bairro ou distrito excluído com sucesso.', 'success');

            const response = await api.get('/neighborhood');
            setNeighborhoods(response.data);

            const totalPages = Math.ceil(response.data.length / itemsPerPage);

            if (page > totalPages && page > 1) {
                setPage(page - 1);
            }

        } catch (error) {
            showSnackbar('Erro ao excluir o bairro ou distrito', 'error');
            console.error('Error deleting neighborhood:', error);
        } finally {
            setOpenConfirmDialog(false);
            setNeighborhoodToDeleteId(null);
        }
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        setNeighborhoodToDeleteId(null);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const isButtonDisabled = () => {
        if (!currentNeighborhood) {
            return !formData.name || !formData.zone;
        } else {
            return formData.name === originalFormData.name && formData.zone === originalFormData.zone;
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth={isMobile ? 'xs' : 'xl'} sx={{ mt: isMobile ? 1 : 0, mb: 4, px: isMobile ? 1 : 2 }}>
                <Grid container spacing={isMobile ? 1 : 3} sx={{ justifyContent: 'center', mt: 0 }}>
                    <Grid item xs={12} sm={10} md={8}>
                        <Card sx={{
                            borderRadius: isMobile ? '8px' : '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            p: isMobile ? 1 : 2,
                            mt: 0
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                pt: isMobile ? 0 : 0
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    justifyContent: 'space-between',
                                    alignItems: isMobile ? 'center' : 'center',
                                    mb: isMobile ? 2 : 3,
                                    width: '100%',
                                    gap: isMobile ? 2 : 0
                                }}>
                                    <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#10641c', fontWeight: '600' }}>
                                        Bairros e Distritos
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpenDialog()}
                                        sx={{
                                            bgcolor: '#10641c',
                                            '&:hover': { bgcolor: '#0a3d0f' },
                                            fontSize: isMobile ? '0.9rem' : '0.875rem',
                                            px: isMobile ? 2 : 3
                                        }}
                                    >
                                        Novo
                                    </Button>
                                </Box>

                                <Divider sx={{ mb: isMobile ? 2 : 2, width: '100%' }} />
                                <Box sx={{ width: '100%', mb: isMobile ? 2 : 2 }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Buscar bairro ou distrito..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        sx={{
                                            height: '45px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: isMobile ? '6px' : '8px',
                                                fontSize: isMobile ? '1rem' : '1rem',
                                                height: 'inherit',
                                                padding: '0 14px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#ced4da',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#10641c',
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#10641c',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: isMobile ? '1rem' : '1rem'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize={isMobile ? 'medium' : 'medium'} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: isMobile ? 2 : 4 }}>
                                        <CircularProgress size={isMobile ? 30 : 40} />
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%' }}>
                                        {isMobile ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {visibleNeighborhood.length === 0 ? (
                                                    <Typography sx={{ fontSize: '1rem', color: '#666', textAlign: 'center' }}>
                                                        Nenhum bairro ou distrito encontrado.
                                                    </Typography>
                                                ) : (
                                                    visibleNeighborhood.map((neighborhood) => (
                                                        <Card key={neighborhood.id} sx={{
                                                            borderRadius: '8px',
                                                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                                                            p: 2
                                                        }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                                                    {neighborhood.name}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '0.9rem' }}>
                                                                    <strong>Zona:</strong> {neighborhood.zone === 'urbana' ? 'Urbana' : neighborhood.zone === 'rural' ? 'Rural' : neighborhood.zone}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                                                                    <Tooltip title="Editar">
                                                                        <IconButton
                                                                            onClick={() => handleOpenDialog(neighborhood)}
                                                                            sx={{ color: '#1976d2', p: 1 }}
                                                                        >
                                                                            <EditIcon fontSize="medium" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Excluir">
                                                                        <IconButton
                                                                            onClick={() => handleDelete(neighborhood.id)}
                                                                            sx={{ color: '#d32f2f', p: 1 }}
                                                                        >
                                                                            <DeleteIcon fontSize="medium" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </Box>
                                                        </Card>
                                                    ))
                                                )}
                                            </Box>
                                        ) : (
                                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                                <TableContainer sx={{ maxWidth: '100%' }}>
                                                    <Table sx={{
                                                        minWidth: 700,
                                                        '& .MuiTableCell-head': { fontWeight: 'bold' },
                                                        '& .MuiTableCell-root': {
                                                            fontSize: '0.875rem',
                                                            p: 1
                                                        }
                                                    }} aria-label="bairro e distrito table">
                                                        <TableHead sx={{ bgcolor: '#f8f8f8' }}>
                                                            <TableRow>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '40%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('name')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Nome
                                                                        {sortConfig.key === 'name' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '20%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('zone')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Zona
                                                                        {sortConfig.key === 'zone' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '20%' }}
                                                                >
                                                                    Ações
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {visibleNeighborhood.length === 0 ? (
                                                                <TableRow>
                                                                    <TableCell colSpan={3} align="center">
                                                                        <Typography sx={{ fontSize: '0.875rem', color: '#666' }}>
                                                                            Nenhum bairro ou distrito encontrado.
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : (
                                                                visibleNeighborhood.map((neighborhood) => (
                                                                    <TableRow
                                                                        key={neighborhood.id}
                                                                        sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}
                                                                    >
                                                                        <TableCell align="center">{neighborhood.name}</TableCell>
                                                                        <TableCell align="center">
                                                                            {neighborhood.zone === 'urbana' ? 'Urbana' : neighborhood.zone === 'rural' ? 'Rural' : neighborhood.zone}
                                                                        </TableCell>
                                                                        <TableCell align="center">
                                                                            <Stack
                                                                                direction="row"
                                                                                spacing={1}
                                                                                justifyContent="center"
                                                                            >
                                                                                <Tooltip title="Editar">
                                                                                    <IconButton
                                                                                        onClick={() => handleOpenDialog(neighborhood)}
                                                                                        sx={{ color: '#1976d2', p: 1 }}
                                                                                    >
                                                                                        <EditIcon fontSize="medium" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title="Excluir">
                                                                                    <IconButton
                                                                                        onClick={() => handleDelete(neighborhood.id)}
                                                                                        sx={{ color: '#d32f2f', p: 1 }}
                                                                                    >
                                                                                        <DeleteIcon fontSize="medium" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </Stack>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {visibleNeighborhood.length > 0 && neighborhoods.length > itemsPerPage && (
                    <Box sx={{ mt: isMobile ? 1 : 2, display: 'flex', justifyContent: 'center' }}>
                        <Paginate
                            count={Math.ceil(neighborhoods.length / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                            size={isMobile ? 'small' : 'medium'}
                        />
                    </Box>
                )}

                {/* Dialog para adicionar/editar Bairro ou Distrito */}
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'xs'}
                    fullScreen={isMobile}
                >
                    <DialogTitle
                        sx={{
                            background: 'linear-gradient(45deg, #10641c 30%, #388e3c 90%)',
                            color: 'white',
                            textAlign: 'center'
                        }}
                    >
                        {currentNeighborhood ? 'Editar Bairro ou Distrito' : 'Cadastrar Bairro ou Distrito'}
                    </DialogTitle>

                    <DialogContent sx={{ mt: isMobile ? 2 : 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: isMobile ? '280px' : '350px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                label="Nome"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                margin="normal"
                                variant="outlined"
                                sx={{
                                    width: '100%',
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#10641c',
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#10641c',
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: isMobile ? '1rem' : '1rem'
                                    }
                                }}
                            />
                            <FormControl margin="normal" variant="outlined" fullWidth sx={{ width: '100%' }}>
                                <InputLabel
                                    sx={{
                                        fontSize: isMobile ? '1rem' : '1rem',
                                        color: 'rgba(0, 0, 0, 0.6)',
                                        '&.Mui-focused': {
                                            color: '#10641c',
                                        },
                                    }}
                                >
                                    Zona
                                </InputLabel>
                                <Select
                                    name="zone"
                                    value={formData.zone}
                                    onChange={handleChange}
                                    label="Zona"
                                    required
                                    sx={{
                                        borderRadius: '5px',
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: formData.zone ? '#10641c' : 'inherit',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#10641c',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#10641c',
                                        },
                                        '& .MuiSelect-select': {
                                            fontSize: isMobile ? '1rem' : '1rem',
                                        },
                                    }}
                                >
                                    <MenuItem value="urbana">Urbana</MenuItem>
                                    <MenuItem value="rural">Rural</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseDialog}
                            sx={{
                                bgcolor: 'red',
                                color: 'white',
                                '&:hover': { bgcolor: '#cc0000' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            sx={{
                                bgcolor: '#10641c',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<SaveIcon />}
                            disabled={isButtonDisabled()}
                        >
                            {currentNeighborhood ? 'Atualizar' : 'Salvar'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de confirmação de exclusão */}
                <Dialog
                    open={openConfirmDialog}
                    onClose={handleCloseConfirmDialog}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'xs'}
                    fullScreen={isMobile}
                >
                    <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', textAlign: "center", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        Confirmar Exclusão
                    </DialogTitle>
                    <DialogContent sx={{ mt: isMobile ? 1 : 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: isMobile ? '1rem' : '1rem' }}>
                            Tem certeza que deseja excluir este bairro ou distrito? <br /> Esta ação é irreversível.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseConfirmDialog}
                            sx={{
                                bgcolor: '#10641c',
                                color: 'white',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            color="error"
                            variant="contained"
                            sx={{
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<DeleteIcon />}
                        >
                            Excluir
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar para feedback */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%', fontSize: isMobile ? '0.9rem' : '0.875rem' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default NeighborhoodPage;